import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@components/ui';
import type { AIPurchaseStoreBreakdownItem } from '../../../types/api.types';
import {
    AI_PURCHASE_STORE_SORT_OPTIONS,
    AI_PURCHASE_STORE_DEFAULT_SORT,
} from '../../../constants/api.constants';
import {
    formatAdoptionPercent,
    formatCount,
    getAdoptionBadgeClasses,
} from './aiPurchaseAnalyticsUtils';

const STORE_SORT_OPTIONS = Object.values(AI_PURCHASE_STORE_SORT_OPTIONS);

interface StoreDrilldownTableProps {
    state: string;
    brandName: string | null;
    stores: AIPurchaseStoreBreakdownItem[];
    loading: boolean;
    error: string | null;
}

/**
 * Compare stores by adoption % by purchases, with higher totalActivePurchases as
 * the tie-break when percentages match. Direction is controlled by `sortValue`.
 */
function compareStoresByAdoptionPurchases(
    a: AIPurchaseStoreBreakdownItem,
    b: AIPurchaseStoreBreakdownItem,
    ascending: boolean
): number {
    const percentDiff = ascending
        ? a.adoptionPercentByPurchases - b.adoptionPercentByPurchases
        : b.adoptionPercentByPurchases - a.adoptionPercentByPurchases;
    if (percentDiff !== 0) return percentDiff;
    // Tie-break: higher totalActivePurchases first (same for both sort directions).
    return b.totalActivePurchases - a.totalActivePurchases;
}

/**
 * Per-store AI-linked vs non-AI-linked active purchase breakdown, within a state
 * and scoped to one brand. Sort is local only (no `sortValue` sent to the API) —
 * sorts the already-fetched stores array in memory by adoption % by purchases.
 */
export default function StoreDrilldownTable({
    state,
    brandName,
    stores,
    loading,
    error,
}: StoreDrilldownTableProps) {
    const [currentSort, setCurrentSort] = useState<string>(AI_PURCHASE_STORE_DEFAULT_SORT);
    const [showSortMenu, setShowSortMenu] = useState(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!showSortMenu) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setShowSortMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSortMenu]);

    const sortedStores = useMemo(() => {
        const ascending =
            currentSort === AI_PURCHASE_STORE_SORT_OPTIONS.ADOPTION_PURCHASES_LOW_TO_HIGH;
        return [...stores].sort((a, b) => compareStoresByAdoptionPurchases(a, b, ascending));
    }, [stores, currentSort]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-[#6e64f0] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-[#dc2626] text-center py-6">{error}</p>;
    }

    if (stores.length === 0) {
        return (
            <p className="text-sm text-[#64748b] text-center py-6">
                No active/trial stores found in {state}
                {brandName ? ` for ${brandName}` : ''} for this date range.
            </p>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <div className="relative" ref={sortMenuRef}>
                    <button
                        type="button"
                        onClick={() => setShowSortMenu((prev) => !prev)}
                        className="px-4 py-2 border border-[#e2e8f0] rounded-lg transition-all flex items-center gap-2 text-[#64748b] hover:bg-[#f8fafc] text-sm font-medium whitespace-nowrap"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                            />
                        </svg>
                        <span className="max-w-[220px] truncate">{currentSort}</span>
                        <svg
                            className="w-3.5 h-3.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    {showSortMenu && (
                        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-20">
                            <div className="p-2">
                                {STORE_SORT_OPTIONS.map((opt) => (
                                    <button
                                        type="button"
                                        key={opt}
                                        onClick={() => {
                                            setCurrentSort(opt);
                                            setShowSortMenu(false);
                                        }}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between text-sm ${
                                            currentSort === opt
                                                ? 'bg-[#f1f5f9] text-[#6e64f0] font-medium'
                                                : 'text-[#64748b] hover:bg-[#f8fafc]'
                                        }`}
                                    >
                                        {opt}
                                        {currentSort === opt && (
                                            <svg
                                                className="w-4 h-4 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Card>
                <CardContent className="py-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[#e2e8f0] text-left text-xs font-medium text-[#64748b] uppercase tracking-wide">
                                <th className="py-2 pr-4">Store</th>
                                {!brandName && <th className="py-2 pr-4">Brand</th>}
                                <th className="py-2 pr-4 text-right">Active</th>
                                <th className="py-2 pr-4 text-right">Line items</th>
                                <th className="py-2 pr-4 text-right">AI-linked</th>
                                <th className="py-2 pr-4 text-right">Non-AI-linked</th>
                                <th className="py-2 pr-4 text-right">AI-linked line items</th>
                                <th className="py-2 pr-4 text-right">Adoption % (purchases)</th>
                                <th className="py-2 text-right">Adoption % (line items)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f1f5f9]">
                            {sortedStores.map((store) => (
                                <tr key={store.storeId} className="text-[#1e293b]">
                                    <td className="py-2.5 pr-4 font-medium">{store.storeName}</td>
                                    {!brandName && (
                                        <td className="py-2.5 pr-4 text-[#64748b]">
                                            {store.brandName ?? '—'}
                                        </td>
                                    )}
                                    <td className="py-2.5 pr-4 text-right">
                                        {formatCount(store.totalActivePurchases)}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right">
                                        {formatCount(store.totalLineItems)}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right">
                                        {formatCount(store.aiLinkedPurchases)}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right">
                                        {formatCount(store.nonAiLinkedPurchases)}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right">
                                        {formatCount(store.aiLinkedLineItems)}
                                    </td>
                                    <td className="py-2.5 pr-4 text-right">
                                        <span
                                            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getAdoptionBadgeClasses(
                                                store.adoptionPercentByPurchases,
                                                store.totalActivePurchases
                                            )}`}
                                        >
                                            {formatAdoptionPercent(
                                                store.adoptionPercentByPurchases,
                                                store.totalActivePurchases
                                            )}
                                        </span>
                                    </td>
                                    <td className="py-2.5 text-right">
                                        <span
                                            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getAdoptionBadgeClasses(
                                                store.adoptionPercentByLineItems,
                                                store.totalLineItems
                                            )}`}
                                        >
                                            {formatAdoptionPercent(
                                                store.adoptionPercentByLineItems,
                                                store.totalLineItems
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
